import axios from 'axios';
import {
  ASSIGN_PROJECTS_TO_ROLES_URL,
  CREATE_ROLE,
  CREATE_ROLE_GROUP,
  UPDATE_ROLE,
  get_request_config
} from '@/utils/system_constants/constant.js';
import { edit_role_form } from './manage_role_helpers';

function role_group_payload(component, name) {
  const payload = {};
  payload.name = name;
  payload.description = component.normalize_text(component.create_group_form.description);
  return payload;
}

function selected_assignment_payload(role_id, selected_project_ids = []) {
  const payload = {};
  payload.role_id = Number(role_id);
  payload.project_ids = selected_project_ids
    .map((project_id) => Number(project_id))
    .filter((project_id) => Number.isInteger(project_id) && project_id > 0);
  return payload;
}

function show_modal(modal_id) {
  const modal_element = document.getElementById(modal_id);
  if (!modal_element) {
    return;
  }

  const modal_instance = window.bootstrap.Modal.getInstance(modal_element) || new window.bootstrap.Modal(modal_element);
  modal_instance.show();
}

export default {
  open_create_role_modal() {
    this.reset_create_form();
  },
  open_create_role_group_modal() {
    this.reset_create_group_form();
  },
  toggle_inventory_tab(tab) {
    this.active_inventory_tab = tab;
  },
  open_edit_role_modal(role) {
    this.edit_form = edit_role_form(role);
    show_modal('edit_role_modal');
  },
  async create_role() {
    const payload = this.build_role_payload(this.create_form);

    if (!payload) {
      return;
    }

    this.creating_role = true;
    const loader = await this.show_loader('Creating role...');

    try {
      const response = await axios.post(CREATE_ROLE, payload, get_request_config());
      const response_data = this.parse_api_data(response.data);

      this.assert_success_response(response_data, 'Role creation failed.');
      this.close_modal('create_role_modal');
      this.reset_create_form();
      await this.fetch_role_groups();
      await this.fetch_roles();
      this.show_success(response_data.message || 'Role created successfully.');
    } catch (error) {
      this.show_error(this.parse_error_message(error, 'Failed to create role.'));
    } finally {
      this.creating_role = false;
      this.close_loader(loader);
    }
  },
  async create_role_group() {
    const name = this.normalize_text(this.create_group_form.name);

    if (!this.validate_role_group_name(name)) {
      return;
    }

    await this.save_role_group(name);
  },
  validate_role_group_name(name) {
    if (!name) {
      this.show_error('Role group name cannot be empty.');
      return false;
    }

    if (this.role_group_name_exists(name)) {
      this.show_error('A role group with this name already exists.');
      return false;
    }

    return true;
  },
  get_role_group_payload(name) {
    return role_group_payload(this, name);
  },
  async post_role_group(name) {
    const response = await axios.post(CREATE_ROLE_GROUP, this.get_role_group_payload(name), get_request_config());
    return this.parse_api_data(response.data);
  },
  assert_success_response(response_data, fallback) {
    if (response_data?.status && String(response_data.status).toLowerCase() !== 'success') {
      throw new Error(response_data.message || fallback);
    }
  },
  async after_role_group_created(response_data) {
    this.close_modal('create_role_group_modal');
    this.reset_create_group_form();
    await this.fetch_role_groups();
    this.show_success(response_data.message || 'Role group created successfully.');
  },
  async save_role_group(name) {
    this.creating_role_group = true;
    const loader = await this.show_loader('Creating role group...');

    try {
      const response_data = await this.post_role_group(name);
      this.assert_success_response(response_data, 'Role group creation failed.');
      await this.after_role_group_created(response_data);
    } catch (error) {
      this.show_error(this.parse_error_message(error, 'Failed to create role group.'));
    } finally {
      this.creating_role_group = false;
      this.close_loader(loader);
    }
  },
  async update_role() {
    const update_request = this.update_role_request();
    if (!update_request) {
      return;
    }

    this.updating_role = true;
    const loader = await this.show_loader('Updating role...');

    try {
      const response = await axios.post(UPDATE_ROLE, update_request.payload, get_request_config());
      const response_data = this.parse_api_data(response.data);

      this.assert_success_response(response_data, 'Role update failed.');
      this.close_modal('edit_role_modal');
      this.reset_edit_form();
      await this.fetch_role_groups();
      await this.fetch_roles();
      this.sync_current_role(update_request.role_id);
      this.show_success(response_data.message || 'Role updated successfully.');
    } catch (error) {
      this.show_error(this.parse_error_message(error, 'Failed to update role.'));
    } finally {
      this.updating_role = false;
      this.close_loader(loader);
    }
  },
  update_role_request() {
    const role_id = Number(this.edit_form.role_id);
    if (!Number.isInteger(role_id) || role_id <= 0) {
      this.show_error('Role id is required.');
      return null;
    }

    const payload = this.build_role_payload(this.edit_form, true);
    return payload ? { role_id, payload } : null;
  },
  sync_current_role(role_id) {
    if (!this.current_role || String(this.current_role.id) !== String(role_id)) {
      return;
    }

    const updated_role = this.roles.find((role) => String(role.id) === String(role_id));
    if (updated_role) {
      this.current_role = { ...updated_role };
    }
  },
  manage_role(role) {
    const assigned_project_ids = Array.isArray(role?.assigned_project_ids)
      ? role.assigned_project_ids.map(String)
      : [];

    this.is_managing_role = true;
    this.current_role = { ...role };
    this.active_role_tab = 'assignments';
    this.selected_project_ids = assigned_project_ids;
    this.original_project_ids = assigned_project_ids;
  },
  close_manage_role() {
    this.is_managing_role = false;
    this.current_role = null;
    this.active_role_tab = 'assignments';
    this.selected_project_ids = [];
    this.original_project_ids = [];
  },
  toggle_role_tab(tab) {
    this.active_role_tab = tab;
  },
  toggle_assignment(project_id) {
    const normalized_project_id = String(project_id);
    const selected_ids = new Set(this.selected_project_ids.map(String));

    if (selected_ids.has(normalized_project_id)) {
      selected_ids.delete(normalized_project_id);
    } else {
      selected_ids.add(normalized_project_id);
    }

    this.selected_project_ids = Array.from(selected_ids);
  },
  is_project_selected(project_id) {
    return this.selected_project_ids.map(String).includes(String(project_id));
  },
  async assign_projects_to_role() {
    const payload = this.assignment_payload();
    if (!payload) {
      return;
    }

    this.assigning_projects = true;
    const loader = await this.show_loader('Saving role assignments...');

    try {
      const response = await axios.post(ASSIGN_PROJECTS_TO_ROLES_URL, payload, get_request_config());
      const response_data = this.parse_api_data(response.data);

      this.assert_success_response(response_data, 'Failed to update project assignments.');
      this.set_assigned_project_ids(payload.project_ids.map(String));
      this.show_success(response_data.message || 'Project assignments updated successfully');
    } catch (error) {
      this.show_error(this.parse_error_message(error, 'Failed to update project assignments.'));
    } finally {
      this.assigning_projects = false;
      this.close_loader(loader);
    }
  },
  assignment_payload() {
    if (!this.current_role) {
      this.show_error('Select a role before managing assignments.');
      return null;
    }

    return this.current_role.id
      ? selected_assignment_payload(this.current_role.id, this.selected_project_ids)
      : null;
  },
  set_assigned_project_ids(assigned_project_ids) {
    this.original_project_ids = assigned_project_ids;
    this.selected_project_ids = assigned_project_ids;
    this.current_role = {
      ...this.current_role,
      assigned_project_ids
    };
    this.roles = this.roles.map((role) =>
      String(role.id) === String(this.current_role.id)
        ? {
          ...role,
          assigned_project_ids
        }
        : role
    );
  }
};
